import { IOperation } from './i-operation';
import { UndoRedoManager } from '../undo-redo.manager';

export class SequenceOperation implements IOperation {
  protected _operations: IOperation[];
  protected _additionalOperations: IOperation[];

  /**
   * The sequence operation needs its own manager for undo/redo operations.
   * Using other manager instances (e.g. the one provided by DI) breaks that manager instance.
   */
  private _undoManager = new UndoRedoManager();

  constructor(
    operations: IOperation[],
    additionalOperations: IOperation[] = []) {
    this._operations = operations;
    this._additionalOperations = additionalOperations || [];
    this._undoManager.undoAndRedoItemLimit = operations.length;
    for (const operation of operations) {
      this._undoManager.pushUndo(operation);
    }
  }

  public getQualifiedClassName(): string {
    return 'SequenceOperation';
  }

  public getOperationDescription(): string {
    return '';
  }

  public get operations(): IOperation[] {
    return this._operations;
  }

  public get additionalOperations(): IOperation[] {
    return this._additionalOperations;
  }

  public async performRedo(): Promise<void> {
    while (this._undoManager.canRedo()) {
      await this._undoManager.redo();
    }
    for (let operation of this._additionalOperations) {
      await operation.performRedo();
    }
  }

  public async performUndo(): Promise<void> {
    for (let operation of this._additionalOperations) {
      await operation.performUndo();
    }
    while (this._undoManager.canUndo()) {
      await this._undoManager.undo();
    }
  }

  public toString(): string {
    return 'Sequence operation: ' + this._undoManager.undoAndRedoItemLimit + ' operations';
  }

  public equals(obj: any): boolean {
    if (
      obj instanceof SequenceOperation &&
      (obj as SequenceOperation).getQualifiedClassName() === this.getQualifiedClassName() &&
      (obj as SequenceOperation).operations.length === this.operations.length &&
      (obj as SequenceOperation).additionalOperations.length === this.additionalOperations.length
    ) {
      const withoutDuplicates: IOperation[] = (obj as SequenceOperation).operations.filter((element: any, index: number, arr: any[]) => {
        const someResult: boolean = this.operations.some((element2: any, index2: number, arr2: any[]) => {
          return this.areEquals(element, element2);
        });
        return !someResult;
      });
      return withoutDuplicates.length === 0;
    } else {
      return false;
    }
  }

  protected areEquals(newOper: any, oldOper: any): boolean {
    if (newOper.equals !== undefined && oldOper.equals !== undefined) {
      return newOper.equals(oldOper);
    } else {
      return newOper === oldOper;
    }
  }
}
